// ════════════════════════════════════════════════════════════════════════
//  Das Dateiformat zwischen zwei Umgebungen
//
//  Staging und Live sind zwei getrennte Supabase-Projekte mit eigenen
//  Schlüsseln. Der Weg dazwischen ist EINE DATEI, die durch einen Menschen
//  geht – hier herunterladen, dort hochladen. Kein Dienst, der beide
//  Umgebungen kennt, keine Zugangsdaten der einen in der anderen. Das ist
//  keine Sparversion, sondern die einzige Form, die keine neue Verbindung
//  zwischen Produktion und Testsystem aufmacht.
//
//  `formatVersion` steht mit Absicht in der Datei. Ohne sie läse eine spätere
//  Anwendung eine ältere Datei still halb – ein paar Felder fehlen, der Rest
//  kommt an, und niemand erfährt davon. Mit ihr ist die Datei entweder die
//  erwartete oder eine erkennbar fremde.
//
//  Die Datei ist symmetrisch: derselbe Weg führte auch von Live zurück nach
//  Staging. Das ist eine Eigenschaft des Formats, kein beworbener Vorgang.
// ════════════════════════════════════════════════════════════════════════

import { z } from "zod";
import type { Bundle, Problem } from "@otto/lehrgang";

export const TRANSFER_FORMAT = "otto-lehrgaenge";
export const TRANSFER_FORMAT_VERSION = 1;

/** Dieselbe Form wie `curricula.slug` im SQL und wie ein Schlüssel im Bundle. */
const slugSchema = z.string().regex(/^[a-z0-9]+(?:[-.][a-z0-9]+)*$/);

/**
 * Ein Lehrgang in der Datei.
 *
 * `requiresSlug` und nicht `requiresId`: ids sind je Umgebung andere, ein Slug
 * meint in beiden denselben Lehrgang. Genau deshalb ist der Slug die Identität
 * und wandert unverändert mit.
 *
 * Das Bundle prüft dieses Schema NICHT selbst – es verlangt nur ein Objekt und
 * reicht es an `parseShape` weiter. Zwei Bundle-Schemata nebeneinander liefen
 * auseinander, und `parseShape` ist die Formprüfung, die überall sonst gilt.
 */
const entrySchema = z.object({
  slug: slugSchema,
  title: z.string().min(1),
  subtitle: z.string().nullable(),
  position: z.number().int(),
  requiresSlug: slugSchema.nullable(),
  /** Welche Fassung in der Quellumgebung – nur für den Herkunftsvermerk. */
  sourceVersion: z.number().int().positive(),
  sourcePublishedAt: z.string(),
  bundle: z.record(z.unknown()),
});

export const transferFileSchema = z.object({
  format: z.literal(TRANSFER_FORMAT),
  formatVersion: z.literal(TRANSFER_FORMAT_VERSION),
  exportedAt: z.string(),
  /** Woher die Datei stammt. Nur zur Nachschau; niemand richtet sich danach. */
  source: z.string(),
  curricula: z.array(entrySchema),
});

export type TransferEntry = z.infer<typeof entrySchema>;
export type TransferFile = z.infer<typeof transferFileSchema>;

/** Ein Lehrgang, wie ihn die Quellumgebung für den Export hergibt. */
export type ExportSource = {
  readonly slug: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly position: number;
  /** Der Slug des Vorgängers, nicht dessen id. */
  readonly requiresSlug: string | null;
  readonly version: number;
  readonly publishedAt: string;
  readonly bundle: Bundle;
};

/**
 * Die Datei aus dem, was die Quellumgebung hergibt.
 *
 * Nach `position` sortiert, damit die Vorschau in der Zielumgebung dieselbe
 * Reihenfolge zeigt wie die Liste, aus der die Datei stammt.
 */
export function buildTransfer(input: {
  readonly source: string;
  readonly exportedAt: Date;
  readonly curricula: readonly ExportSource[];
}): TransferFile {
  return {
    format: TRANSFER_FORMAT,
    formatVersion: TRANSFER_FORMAT_VERSION,
    exportedAt: input.exportedAt.toISOString(),
    source: input.source,
    curricula: [...input.curricula]
      .sort((a, b) => a.position - b.position)
      .map((c) => ({
        slug: c.slug,
        title: c.title,
        subtitle: c.subtitle,
        position: c.position,
        requiresSlug: c.requiresSlug,
        sourceVersion: c.version,
        sourcePublishedAt: c.publishedAt,
        // Das Bundle wandert UNVERÄNDERT – Schlüssel wie Slug. Es ist derselbe
        // Lehrgang in einer anderen Umgebung, keine Kopie.
        bundle: c.bundle as unknown as Record<string, unknown>,
      })),
  };
}

/**
 * Eine Datei lesen.
 *
 * Gibt eine Problemliste zurück statt zu werfen: wer eine falsche Datei wählt,
 * soll lesen, was daran falsch ist, und nicht einen roten Kasten ohne Grund
 * sehen.
 */
export function readTransfer(
  text: string,
): { readonly file: TransferFile } | { readonly problems: readonly Problem[] } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return {
      problems: [{ path: "datei", message: "Das ist keine gültige JSON-Datei." }],
    };
  }

  // Format und Formatversion zuerst und von Hand. Zod meldete hier
  // „Invalid literal value, expected 1“ – richtig, aber unbrauchbar für
  // jemanden, der eine drei Monate alte Datei erwischt hat.
  const head = raw as { format?: unknown; formatVersion?: unknown } | null;
  if (head?.format !== TRANSFER_FORMAT) {
    return {
      problems: [
        {
          path: "format",
          message:
            "Das ist keine Lehrgangsdatei aus dieser Anwendung. Erwartet wird eine Datei, " +
            "die der Export unter „Übertragen“ erzeugt hat.",
        },
      ],
    };
  }
  if (head.formatVersion !== TRANSFER_FORMAT_VERSION) {
    return {
      problems: [
        {
          path: "formatVersion",
          message:
            `Die Datei ist im Format ${String(head.formatVersion)} geschrieben, gelesen ` +
            `wird Format ${TRANSFER_FORMAT_VERSION}. Erzeugen Sie den Export in der ` +
            `Quellumgebung neu.`,
        },
      ],
    };
  }

  const parsed = transferFileSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      problems: parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    };
  }
  return { file: parsed.data };
}

/**
 * Der Dateiname.
 *
 * Das Datum in **Europe/Berlin**: der Browser der Redaktion steht dort, und
 * eine Datei, die um 01:30 den Vortag im Namen trägt, findet abends niemand
 * wieder. `sv-SE` liefert genau `2026-09-05` – die kürzeste Art, ein
 * ISO-Datum in einer bestimmten Zeitzone zu bekommen.
 */
export function transferFileName(at: Date): string {
  const datum = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(
    at,
  );
  return `otto-lehrgaenge-${datum}.json`;
}

/** Ein Zeitpunkt, wie ihn die Redaktion liest: `04.09.2026`, in Europe/Berlin. */
export function berlinDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(at);
}
